/**
 * The artifacts of a chat transcript — the durable things a turn produced that the user can go and open.
 *
 * One model feeds the inline card, the compact copy pinned once that card scrolls past (ACT2-538), and the
 * header "Artifacts" menu. Collection walks the SAME grouped messages MessageList renders, in render order,
 * via the same helpers ActivityGroup uses, so the pin can never advertise a card from elsewhere.
 *
 * Routines are deliberately not artifacts: a `workflow_preview` is a dry-run result whose content does not
 * fit a one-line card, and its payload carries no schedule/channel identity to key on.
 */
import { isFiniteNumber } from '@/_shared/lib/numeric';
import type { SSEMessage } from '@/_shared/types';
import type { CountState } from '@/audiences/types';

import type { GroupedMessage } from '../components/tool-call-meta';
import { dashboardArtifactCardsFromItems, normalizeDashboardActivityItems } from './dashboard-activity';
import type { ResolvedDashboardArtifact } from './resolved-dashboard-artifact';

/** Stamped on each inline card wrapper so the observer can identify a card without knowing its type. */
export const CHAT_ARTIFACT_KEY_ATTRIBUTE = 'data-chat-artifact-key';

/** Spread onto a card's wrapper element to make it observable by key. */
export function chatArtifactKeyProps(key: string): Record<string, string> {
  return { [CHAT_ARTIFACT_KEY_ATTRIBUTE]: key };
}

export type ChatArtifactType = 'dashboard' | 'audience';

/**
 * Identifies ONE rendered card by its position. Per-card, not per-group: two dashboards in a single
 * activity group must be distinguishable, or the pin cannot tell which of them it is observing.
 */
export function chatArtifactSourceId(groupIndex: number, cardIndex: number): string {
  return `${groupIndex}.${cardIndex}`;
}

interface ChatArtifactBase {
  /** Identity for pinning, de-dup and menu rows. Type-prefixed so ids from two namespaces cannot collide. */
  key: string;
  title: string;
  /** Source id of the LAST card for this identity — the only one the pin observes. */
  stickySourceId: string;
  /** Render position of that observed card. Strictly increasing, so the pin ordering has no ties. */
  order: number;
}

export interface DashboardChatArtifact extends ChatArtifactBase {
  type: 'dashboard';
  slug: string;
  saved: boolean;
  /** Only true for unsaved previews — not for saved or not-found dashboards. */
  isDraft: boolean;
  dashboardId?: string;
  /** Panels in the spec; omitted when neither the payload nor a hydrated live spec carried one. */
  panelCount?: number;
  /** The in-chat preview can be opened for this slug (live store entry, or saved and hydratable). */
  previewAvailable: boolean;
}

export interface AudienceChatArtifact extends ChatArtifactBase {
  type: 'audience';
  /** Null for a malformed payload — the card then shows a disabled CTA rather than a dead link. */
  audienceId: string | null;
  countState: CountState;
  status: 'draft' | 'saved';
}

export type ChatArtifact = DashboardChatArtifact | AudienceChatArtifact;

export function chatArtifactKey(type: ChatArtifactType, id: string): string {
  return `${type}:${id}`;
}

// ─── audience_artifact ───

interface AudienceArtifactMeta {
  audienceId?: unknown;
  title?: unknown;
  members?: unknown;
  universe?: unknown;
  percentage?: unknown;
  status?: unknown;
}

function readMeta(metadata: unknown): AudienceArtifactMeta {
  return metadata !== null && typeof metadata === 'object' ? metadata : {};
}

function toCountState(meta: AudienceArtifactMeta): CountState {
  // `isFiniteNumber`, not `typeof === 'number'`: NaN and Infinity are numbers, and would reach the card as
  // "NaN (NaN% of NaN)". An unusable count is better shown as uncounted than as nonsense. The predicate
  // form narrows, so the fields stay type-checked rather than cast back to `number` on trust.
  if (isFiniteNumber(meta.members) && isFiniteNumber(meta.universe) && isFiniteNumber(meta.percentage)) {
    return { status: 'counted', members: meta.members, universe: meta.universe, percentage: meta.percentage };
  }
  return { status: 'not-run' };
}

/**
 * Parse one `audience_artifact` ui_message. Every field is type-checked rather than cast, so a malformed
 * payload degrades to the `content` title and a `not-run` count instead of reaching JSX. `index` keys only
 * an id-less payload: two malformed payloads are not knowably the same audience.
 */
export function toAudienceChatArtifact(
  content: string,
  metadata: unknown,
  index: number,
  order = index,
): AudienceChatArtifact {
  const meta = readMeta(metadata);
  const audienceId = typeof meta.audienceId === 'string' && meta.audienceId.length > 0 ? meta.audienceId : null;
  return {
    type: 'audience',
    key: chatArtifactKey('audience', audienceId ?? `index:${index}`),
    audienceId,
    title: typeof meta.title === 'string' && meta.title.length > 0 ? meta.title : content,
    countState: toCountState(meta),
    // `save_audience_draft` is the only emitter and it creates drafts; the field exists so the card and the
    // menu row read one source rather than each hardcoding a status.
    status: meta.status === 'saved' ? 'saved' : 'draft',
    stickySourceId: chatArtifactSourceId(index, 0),
    order,
  };
}

// ─── collection ───

export interface CollectChatArtifactsOptions {
  /** Slugs already persisted before each message index — see `buildDashboardSavedSlugsPrefix`. */
  savedSlugsPrefix: Set<string>[];
  /** Live-store overlay, so a card reflects a title/status the store knows and the transcript does not. */
  resolveDashboardArtifact?: (slug: string) => ResolvedDashboardArtifact | null;
}

/**
 * Every artifact in the transcript, in the order its card appears (oldest first).
 *
 * De-duplicated by key: re-previewing the same dashboard refreshes that entry in place but keeps its
 * original menu position (Map preserves first-insert order), while `stickySourceId`/`order` move to the
 * latest card so the pin follows the edit rather than the first mention.
 */
export function collectChatArtifacts(
  groups: GroupedMessage[],
  { savedSlugsPrefix, resolveDashboardArtifact }: CollectChatArtifactsOptions,
): ChatArtifact[] {
  const byKey = new Map<string, ChatArtifact>();
  let order = 0;

  for (const group of groups) {
    if (group.kind === 'activity') {
      // Derived exactly as ActivityGroup derives the cards it renders, so the two cannot disagree.
      const items = normalizeDashboardActivityItems(group.items, savedSlugsPrefix[group.startIndex] ?? new Set());
      const cards = dashboardArtifactCardsFromItems(items, resolveDashboardArtifact);
      for (const [cardIndex, card] of cards.entries()) {
        const key = chatArtifactKey('dashboard', card.slug);
        byKey.set(key, {
          type: 'dashboard',
          key,
          title: card.title,
          slug: card.slug,
          saved: card.saved,
          isDraft: card.isDraft,
          previewAvailable: !!resolveDashboardArtifact?.(card.slug) || card.saved,
          stickySourceId: chatArtifactSourceId(group.startIndex, cardIndex),
          order: order++,
          ...(card.dashboardId ? { dashboardId: card.dashboardId } : {}),
          ...(card.panelCount === undefined ? {} : { panelCount: card.panelCount }),
        });
      }
      continue;
    }

    const message: SSEMessage = group.message;
    if (message.type !== 'ui_message') continue;
    if (message.uiType === 'audience_artifact') {
      const artifact = toAudienceChatArtifact(message.content, message.metadata, group.index, order++);
      byKey.set(artifact.key, artifact);
    }
  }

  return [...byKey.values()];
}

/** A live-store dashboard, reduced to what the artifact list needs. Keeps this module free of store types. */
export interface StoreDashboardEntry {
  slug: string;
  title: string;
  saved: boolean;
  isDraft: boolean;
  dashboardId?: string;
  panelCount?: number;
}

/**
 * Append dashboards the live store holds but the transcript never recorded.
 *
 * The transcript only yields a card once a preview/save tool_result lands, while the store is populated
 * by the `dashboard_draft` SSE event that precedes it. An aborted turn or dropped stream leaves the
 * dashboard open in the preview panel yet absent from every list — this is what keeps it reachable.
 *
 * These carry a `store.` source id no rendered card can match, so they never become the pin's source;
 * they are list entries only.
 */
export function withStoreOnlyDashboards(
  fromTranscript: ChatArtifact[],
  storeEntries: StoreDashboardEntry[],
): ChatArtifact[] {
  const seen = new Set(fromTranscript.map((artifact) => artifact.key));
  // Continue past the HIGHEST order issued, not past the array length: a re-previewed dashboard consumes
  // an ordinal without taking an array slot, so `length` would collide with a real card and break the
  // strictly-increasing order `chatArtifactPinInputs` sorts on.
  const lastOrder = fromTranscript.reduce((max, artifact) => Math.max(max, artifact.order), -1);
  const storeOnly = storeEntries
    .filter((entry) => !seen.has(chatArtifactKey('dashboard', entry.slug)))
    .map(
      (entry, index): ChatArtifact => ({
        type: 'dashboard',
        key: chatArtifactKey('dashboard', entry.slug),
        title: entry.title,
        slug: entry.slug,
        saved: entry.saved,
        isDraft: entry.isDraft,
        previewAvailable: true,
        stickySourceId: `store.${entry.slug}`,
        order: lastOrder + 1 + index,
        ...(entry.dashboardId === undefined ? {} : { dashboardId: entry.dashboardId }),
        ...(entry.panelCount === undefined ? {} : { panelCount: entry.panelCount }),
      }),
    );
  return [...fromTranscript, ...storeOnly];
}

/** What the pin needs, derived in one pass so the three shapes cannot disagree about identity or order. */
export interface ChatArtifactPinInputs {
  /** Keys in the physical order of the observed cards — what `pickStickyKey` walks from the end. */
  keys: string[];
  /** Identity → source id of the card the pin observes. Earlier cards must not carry the key attribute. */
  sources: ReadonlyMap<string, string>;
  /** Changes when an observed NODE moves even if the keys do not, so the hook re-binds the observer. */
  epoch: string;
  byKey: ReadonlyMap<string, ChatArtifact>;
}

export function chatArtifactPinInputs(artifacts: ChatArtifact[]): ChatArtifactPinInputs {
  const observed = [...artifacts].sort((a, b) => a.order - b.order);
  return {
    keys: observed.map((artifact) => artifact.key),
    sources: new Map(artifacts.map((artifact) => [artifact.key, artifact.stickySourceId])),
    epoch: observed.map((artifact) => `${artifact.key}@${artifact.stickySourceId}`).join('\u0000'),
    byKey: new Map(artifacts.map((artifact) => [artifact.key, artifact])),
  };
}
